import {
  Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges, ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import Course, { getSubmittableCourseRequirements, isDisallowedSchoolCounselorRequirement } from '../../course.class';
import { CoursesService } from 'src/app/services/courses.service';
import { RequirementsService } from 'src/app/services/requirements.service';

function apiChildrenToFrontend(children: any[]): any[] {
  if (!children || !Array.isArray(children)) return [];
  return children.map((c: any) => ({
    name: c.requirement,
    requirement: c.requirement,
    Id: c.requirementId,
    requirementId: c.requirementId,
    parentId: c.parentRequirementId,
    topMostParentId: c.topMostParentId,
    topMostParentName: c.topMostParentName,
    nodeType: c.nodeType,
    childCount: c.childCount,
    Requirement: c.Requirement,
    children: apiChildrenToFrontend(c.Children || c.children || []),
  }));
}


@Component({
  selector: 'app-course-sorting',
  templateUrl: './course-sorting.component.html',
  styleUrls: ['./course-sorting.component.scss'],

})
export class CourseSortingComponent implements OnInit, OnChanges {
  @ViewChild('requirementsinput') requirementsinput: any;
  @ViewChild('tagsinput') tagsinput: any;
  @ViewChild('categoriesinput') categoriesinput: any;
  @Output() stepForward = new EventEmitter<boolean>();
  @Output() stepBack = new EventEmitter<any>();
  @Output() closeDialogEmitter = new EventEmitter<any>();
  @Output() stateRequirementValidationChange = new EventEmitter<{ isRequired: boolean; isValid: boolean }>();
  @Input() course: Course;
  @Input() requirements: Array<any>;
  @Input() previousYearRequirements: Array<any>;
  @Input() tags: Array<any>;
  @Input() categories: Array<any>;
  @Input() isEditMode: boolean;

  RequirementList: Array<any>;

  // State requirements
  stateRequirements: Array<any> = [];
  showStateRequirementSection = false;
  isStateRequirementRequired = false;

  // Tracks GROUP_PARENT IDs whose children are currently being fetched so we
  // don't issue duplicate HTTP requests when ngOnChanges fires multiple times.
  private _prefetchedParentIds = new Set<number>();

  constructor(
    public dialog: MatDialog, private coursesService: CoursesService, private requirementsService: RequirementsService,
    private cdr: ChangeDetectorRef,
  ) { }


  ngOnInit(): void {
    // Initialize requirements if not provided
    this.requirements = this.requirements || [];
    this.RequirementList = this.newRequirementList(this.course?.requirementsList ?? []);
    // Load state requirements first, then check if section should show
    // checkIfStateRequirementSectionShouldShow() will be called after state requirements load
    this.loadStateRequirements();
    // Emit initial validation status (will be updated after state requirements load)
    this.emitStateRequirementValidation();
    // Pre-fetch children if both inputs are already available at init time
    if (this.requirements?.length > 0 && this.course?.requirementsList?.length > 0) {
      this.prefetchMissingChildren();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When the course input changes (e.g., when editing an existing course)
    if (changes['course'] && changes['course'].currentValue) {
      this.RequirementList = this.newRequirementList(this.course?.requirementsList ?? []);
      // If state requirements are already loaded, check if section should show
      // Otherwise, checkIfStateRequirementSectionShouldShow will be called after state requirements load
      if (this.stateRequirements.length > 0) {
        this.checkIfStateRequirementSectionShouldShow();
      } else {
        // If state requirements haven't loaded yet, load them now
        // This ensures we have the data to display the pre-selected value
        this.loadStateRequirements();
      }
      // When course loads, pre-fetch children for any GROUP_PARENTs whose children
      // are referenced in requirementsList but not yet present in the requirements dataset.
      if (this.requirements?.length > 0) {
        this.prefetchMissingChildren();
      }
    }

    // When requirements input changes
    if (changes['requirements'] && changes['requirements'].currentValue) {
      this.requirements = changes['requirements'].currentValue || [];
      // When requirements first arrive, check if existing course requirements need
      // their GROUP_PARENT children loaded so the tree can display them.
      if (this.course?.requirementsList?.length > 0) {
        this.prefetchMissingChildren();
      }
    }
  }

  /**
   * For every GROUP_PARENT referenced in the course's saved requirementsList whose
   * children are not yet loaded into the `requirements` dataset, fetch those children
   * and merge them in. Once merged, `requirements` is spread into a new array which
   * triggers ngOnChanges in tree-checklist → updateDataSource → the tree renders
   * School Counselor's children and preSetCheckboxes can check them correctly.
   */
  private prefetchMissingChildren(): void {
    const requirementsList = this.course?.requirementsList ?? [];
    if (!requirementsList.length || !this.requirements?.length) return;

    const parentIdsToFetch = new Set<number>();

    requirementsList.forEach((req: any) => {
      const nodeType = req.nodeType ?? req.node_type;
      const reqId = Number(req.requirementId ?? req.Id ?? req.id);

      if (nodeType === 'GROUP_PARENT') {
        // The item itself is the GROUP_PARENT — check if its children are loaded
        const parent = this.findRequirement(this.requirements, reqId);
        if (parent && !parent.children?.length && Number(parent.childCount) > 0
          && !this._prefetchedParentIds.has(reqId)) {
          parentIdsToFetch.add(reqId);
        }
        return;
      }

      // For child nodes find their topMostParent and check if it has children loaded
      const topMostParentId = Number(req.topMostParentId ?? req.top_most_parent_id ?? 0);
      if (topMostParentId && !this._prefetchedParentIds.has(topMostParentId)) {
        const parent = this.findRequirement(this.requirements, topMostParentId);
        if (parent && parent.nodeType === 'GROUP_PARENT'
          && !parent.children?.length && Number(parent.childCount) > 0) {
          parentIdsToFetch.add(topMostParentId);
        }
      }
    });

    parentIdsToFetch.forEach(parentId => {
      this._prefetchedParentIds.add(parentId);
      this.requirementsService.getRequirementDetails(parentId, 'AllChildrenDetails').subscribe({
        next: (response: any) => {
          const req = response?.data ?? response;
          const children = req?.Children ?? req?.children ?? [];
          const childrenFormatted = apiChildrenToFrontend(children);
          this.mergeChildrenIntoRequirement(parentId, childrenFormatted);
          this.cdr.detectChanges();
        },
        error: () => {},
      });
    });
  }

  loadStateRequirements = () => {
    this.coursesService.getStateRequirements().subscribe({
      next: (data: any) => {
        if (data && data.data && data.data.rows) {
          this.stateRequirements = data.data.rows.map((stateReq: any) => ({
            value: stateReq.stateRequirement, // Use name as value instead of ID
            text: stateReq.stateRequirement,
            id: stateReq.id,
          }));
        } else {
          // If no data returned, clear state requirements array
          this.stateRequirements = [];
        }
        // Re-evaluate if section should show after loading state requirements
        this.checkIfStateRequirementSectionShouldShow();
        // Update the input value if there's a pre-selected state requirement
        this.updateStateRequirementInputValue();
      },
      error: (error: any) => {
        // On error, clear state requirements and re-evaluate
        this.stateRequirements = [];
        this.checkIfStateRequirementSectionShouldShow();
      },
    });
  }

  updateStateRequirementInputValue = () => {
    // Trigger change detection to update the input value binding
    // The [value] binding in the template uses getSelectedStateRequirementText()
    // which will now return the correct value since stateRequirements is populated
    this.cdr.detectChanges();
  }

  checkIfStateRequirementSectionShouldShow = () => {
    // Check if any of the selected requirements have reportToStateFlag = 1
    const hasReportToStateRequirement = this.course?.requirementsList?.some((req: any) => {
      // Check if the requirement has reportToStateFlag = 1
      return req.Requirement?.reportToStateFlag === 1 || req.reportToStateFlag === 1;
    }) || false;

    // Only show if:
    // 1. A report-to-state requirement is selected
    // 2. AND state requirements API returned data
    this.showStateRequirementSection = hasReportToStateRequirement && this.stateRequirements.length > 0;

    // Make it required only if both conditions are met
    this.isStateRequirementRequired = hasReportToStateRequirement && this.stateRequirements.length > 0;

    // If section should not show, clear the state requirement
    if (!this.showStateRequirementSection) {
      this.course.stateRequirementId = null;
    }

    // Emit validation status to parent component
    this.emitStateRequirementValidation();
  }

  emitStateRequirementValidation = () => {
    const isValid = !this.isStateRequirementRequired || (this.isStateRequirementRequired && !!this.course?.stateRequirementId);
    this.stateRequirementValidationChange.emit({
      isRequired: this.isStateRequirementRequired,
      isValid: isValid,
    });
  }

  updateLocalCourseParameter = (event: any, parameter: 'requirements' | 'tags' | 'categories') => {
    let selectedValue: any;

    if (parameter === 'requirements') {
      this.course.requirementsList = [];
      this.course.edited = true;
      this.RequirementList = [];
      const raw = Array.from(event[0] || []);
      // Dedupe by id so single requirements (and tree duplicate emits) don't show twice in review/finish
      const arr = raw.filter((id: any, i: number) => raw.findIndex((x: any) => Number(x) === Number(id)) === i);
      const idsNeedingFetch: number[] = [];

      for (let i = 0; i < arr.length; i++) {
        selectedValue = this.findRequirement(this.requirements, arr[i]);
        if (!selectedValue) continue;
        const hasChildCount = Number(selectedValue?.childCount) > 0;
        const hasNoChildren = !selectedValue?.children?.length && !selectedValue?.Children?.length;
        if (selectedValue.nodeType === 'GROUP_PARENT' && hasChildCount && hasNoChildren) {
          idsNeedingFetch.push(Number(selectedValue.Id ?? selectedValue.requirementId ?? arr[i]));
        }
      }

      if (idsNeedingFetch.length > 0) {
        this.fetchAndMergeSubRequirements(arr, idsNeedingFetch);
        return;
      }
      this.applyRequirementsSelection(arr);
    }
    if (parameter === 'tags') {
      selectedValue = this.tags.find((item: any) => item.tag.trim() === event.target.value.trim());
      this.tagsinput.nativeElement.value = '';
    }
    if (parameter === 'categories') {
      selectedValue = this.categories.find((item: any) => item.category === event.target.value);
      this.categoriesinput.nativeElement.value = '';
    }

    if (!selectedValue) { return }
    this.course.addToArray(parameter, selectedValue);
  }

  private fetchAndMergeSubRequirements(selectedIds: any[], parentIds: number[]): void {
    let completed = 0;
    parentIds.forEach((parentId) => {
      this.requirementsService.getRequirementDetails(parentId, 'AllChildrenDetails').subscribe({
        next: (response: any) => {
          const req = response?.data ?? response;
          const children = req?.Children ?? req?.children ?? [];
          const childrenFormatted = apiChildrenToFrontend(children);
          this.mergeChildrenIntoRequirement(parentId, childrenFormatted);
          completed++;
          if (completed === parentIds.length) {
            this.applyRequirementsSelection(this.expandIdsWithDescendants(selectedIds));
            this.cdr.detectChanges();
          }
        },
        error: () => {
          completed++;
          if (completed === parentIds.length) {
            this.applyRequirementsSelection(this.expandIdsWithDescendants(selectedIds));
            this.cdr.detectChanges();
          }
        },
      });
    });
  }

  private expandIdsWithDescendants(ids: any[]): any[] {
    const expanded = new Set(ids.map((id: any) => Number(id)));
    ids.forEach((id: any) => {
      const req = this.findRequirement(this.requirements, id);
      if (req && (req.nodeType === 'GROUP_PARENT' || req.nodeType === 'GROUP_NODE')) {
        [...this.collectGroupLeaves(req), ...this.collectGroupNodesUnder(req)].forEach((desc: any) => {
          expanded.add(Number(desc.Id ?? desc.requirementId));
        });
      }
    });
    return Array.from(expanded);
  }

  private mergeChildrenIntoRequirement(parentId: number, childrenFormatted: any[]): void {
    const parent = this.findRequirement(this.requirements, parentId);
    if (parent && childrenFormatted?.length) {
      parent.children = childrenFormatted;
      this.requirements = this.requirements ? [...this.requirements] : [];
    }
  }

  private applyRequirementsSelection(arr: any[]): void {
    for (let i = 0; i < arr.length; i++) {
      const selectedValue = this.findRequirement(this.requirements, arr[i]);
      if (!selectedValue) continue;

      // Only link bottom-most child requirements (SINGLE or GROUP_LEAF) to the course.
      // GROUP_PARENT and GROUP_NODE are structural hierarchy nodes and must not be saved
      // as course requirements — they appear in Course Push dropdowns otherwise.
      const isLeafLevel = selectedValue.nodeType === 'SINGLE' || selectedValue.nodeType === 'GROUP_LEAF';
      if (!isLeafLevel || isDisallowedSchoolCounselorRequirement(selectedValue) || this.isRequirementinList(this.course.requirementsList, arr[i])) continue;

      this.course.requirementsList = [...this.course.requirementsList, selectedValue];
    }
    const seen = new Set<number>();
    this.course.requirementsList = this.course.requirementsList.filter((r: any) => {
      const id = Number(r?.Id ?? r?.requirementId ?? r?.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    this.RequirementList = this.newRequirementList(this.course.requirementsList);
    this.checkIfStateRequirementSectionShouldShow();

    if (this.requirementsinput) {
      this.requirementsinput.checkedIds = [];
    }
  }

  findRequirement(dataSet: any[], value: any): any {
    if (!dataSet || !Array.isArray(dataSet)) return undefined;
    const val = value != null ? String(value) : null;
    for (const requirement of dataSet) {
      if (val && (String(requirement?.Id) === val || String(requirement?.requirementId) === val)) {
        return requirement;
      }
      if (Array.isArray(requirement.children) && requirement.children.length > 0) {
        const found = this.findRequirement(requirement.children, value);
        if (found) return found;
      }
    }
    return undefined;
  }

  /** Collect all GROUP_LEAF descendants from a requirement node */
  collectGroupLeaves(node: any): any[] {
    if (!node) return [];
    const leaves: any[] = [];
    if (node.nodeType === 'GROUP_LEAF') {
      leaves.push(node);
    }
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach((child: any) => leaves.push(...this.collectGroupLeaves(child)));
    }
    return leaves;
  }

  /** All GROUP_NODE descendants under a GROUP_PARENT (not including the root). */
  collectGroupNodesUnder(node: any): any[] {
    if (!node || !Array.isArray(node.children) || node.children.length === 0) return [];
    const out: any[] = [];
    for (const child of node.children) {
      if (child.nodeType === 'GROUP_NODE') {
        out.push(child);
      }
      out.push(...this.collectGroupNodesUnder(child));
    }
    return out;
  }

  isSubRequirementChip(requirement: any): boolean {
    if (!requirement) return false;
    const t = requirement.nodeType;
    if (t === 'GROUP_PARENT' || t === 'SINGLE') return false;
    if (t === 'GROUP_LEAF' || t === 'GROUP_NODE') return true;
    const label = (requirement.requirement || requirement.Requirement?.requirement || '').trim();
    return !!label;
  }

  isRequirementinList(dataSet: any[], value: any) {
    const id = Number(value);
    return dataSet.some((req) => {
      const reqId = Number(req?.Id ?? req?.requirementId ?? req?.id);
      return reqId === id || req.Id === value || req.requirementId === value;
    });
  }

  newRequirementList(dataSet: any[]) {
    const parents = dataSet.reduce((acc, element) => {
      const requirement = element.topMostParentId;
      (acc[requirement] = acc[requirement] || []).push(element);
      return acc;

    }, {});
    return Object.values(parents);
  }
  checkNextStepDisabled = () => {
    // Check if requirements are present
    const hasRequirements = getSubmittableCourseRequirements(this.course?.requirementsList ?? []).length > 0;
    if (!hasRequirements) {
      return true;
    }

    // Check state requirement validation: if required but not valid, disable next step
    if (this.isStateRequirementRequired && !this.course?.stateRequirementId) {
      return true;
    }

    return false;
  }

  updateCourse = () => {
    // Only proceed if validation passes
    if (this.checkNextStepDisabled()) {
      return;
    }
    this.stepForward.emit(true);
  }

  stepBackward = () => {
    this.stepBack.emit();
  }

  removeAssetFromArray = (asset: any, param: 'categories' | 'requirements' | 'tags') => {
    const id = asset.requirementId ?? asset.Id ?? asset.id;
    this.course.removeFromArray(param, id);

    if (param === 'requirements') {
      const seen = new Set<number>();
      this.course.requirementsList = this.course.requirementsList.filter((r: any) => {
        const rid = Number(r?.Id ?? r?.requirementId ?? r?.id);
        if (seen.has(rid)) return false;
        seen.add(rid);
        return true;
      });
    }

    this.RequirementList = this.newRequirementList(this.course.requirementsList);

    if (param === 'requirements') {
      this.checkIfStateRequirementSectionShouldShow();
    }
  }

  removeAllFromArray = (asset: any, param: 'categories' | 'requirements' | 'tags') => {
    const requirementsToRemove = [];
    for (let i = 0; i < this.RequirementList.length; i++) {
      const req = this.RequirementList[i]
      if (req.length === 1 && req[0].topMostParentId === asset.topMostParentId) {
        requirementsToRemove.push(req[0]);
      }
      else {
        for (let x = 0; x < req.length; x++) {
          if (req[x].topMostParentId === asset.topMostParentId) {
            requirementsToRemove.push(req[x]);
          }
        }
      }
    }
    for (let i = 0; i < requirementsToRemove.length; i++) {
      const id = requirementsToRemove[i].requirementId;
      this.course.removeFromArray(param, id);
    }
    this.RequirementList = this.newRequirementList(this.course.requirementsList);

    // If removing requirements, check if state requirement section should still be shown
    if (param === 'requirements') {
      this.checkIfStateRequirementSectionShouldShow();
    }
  }


  onStateRequirementChange = (event: any) => {
    // Find the state requirement by name and store the ID
    const selectedStateReq = this.stateRequirements.find(req => req.value === event.target.value);
    this.course.stateRequirementId = selectedStateReq ? selectedStateReq.id : null;
    this.course.edited = true;
    // Emit validation status after change
    this.emitStateRequirementValidation();
    // Force change detection to update button state immediately
    this.cdr.detectChanges();
  }

  getSelectedStateRequirementText = () => {
    if (!this.course?.stateRequirementId || !this.stateRequirements || this.stateRequirements.length === 0) {
      return '';
    }
    // Convert both to numbers for comparison to handle string/number mismatches
    const courseStateReqId = Number(this.course.stateRequirementId);
    const selectedStateReq = this.stateRequirements.find(req => Number(req.id) === courseStateReqId);
    return selectedStateReq ? selectedStateReq.text : '';
  }

  closeDialog = () => {
    this.closeDialogEmitter.emit();
  }

  getStateRequirementHelpText = () => {
    return '<strong>Available options:</strong><br><br><strong>Classroom Management:</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;• Engaging students<br>&nbsp;&nbsp;&nbsp;&nbsp;• Norms for social interaction<br>&nbsp;&nbsp;&nbsp;&nbsp;• Student, parent and community communications<br><br><strong>Instructional Delivery (Strategies):</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;• Peer coaching<br>&nbsp;&nbsp;&nbsp;&nbsp;• Growth/assessment strategies<br>&nbsp;&nbsp;&nbsp;&nbsp;• Best practices<br>&nbsp;&nbsp;&nbsp;&nbsp;• Differentiating instruction<br>&nbsp;&nbsp;&nbsp;&nbsp;• Remediation and/or extension<br>&nbsp;&nbsp;&nbsp;&nbsp;• Designing effective lessons';
  }

}
